/*
 * Copyright (c) Nexign, JSC, 2021-2025.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Alexander Golshtein
 * @author Nikolay Sviridov
 * @version 1.36.1
 * @since 1.14.1
 */

import { createElement } from 'react';

import isEmpty from 'lodash/isEmpty';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { makeSelectorInitBasketList } from '@/main/components/Table/components/footerModal/Basket/basketSelectors';
import { makeSelectorBasketContext } from '@/main/components/Table/selectors/tableSelectors';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { removeEmptyValuesFromObject } from '@/main/utils/removeEmptyValues';
import { HttpStatus } from '@modules/app/appConstants';
import { makeSelectBreadCrumbsData } from '@modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@modules/breadCrumbs/utils/breadCrumbsGoBack';
import {
    makeSelectTranslationCommon,
    makeSelectWizardTranslation,
} from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { IWizardTranslation } from '@modules/internationalization/types/wizards/wizard';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import ModalErrorCommon from '@modules/modal/components/ModalError';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { EWizardReportParam } from '@modules/wizard/components/WizardReport/WizardReportConstants';
import { wizardReportStorageRestoreSaga ,
    wizardReportValidateSaga,
} from '@modules/wizard/components/WizardReport/wizardReportSagas';
import {
    makeSelectWizardReportOrderParams,
    makeSelectWizardReportParam,
} from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import {
    IWizardReportOrderParams,
    IWizardReportParam,
} from '@modules/wizard/components/WizardReport/wizardReportTypes';
import { wizardNextAction, wizardStorageSaveStepAction } from '@modules/wizard/wizardActions';
import {
    WIZARD_BACK_ACTION,
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_ACTION,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_REPORT_SET_PARAM,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
} from '@modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction, TWizardStepNames } from '@modules/wizard/wizardTypes';

import { makeSelectFeatureEnabled } from '../PageAuth/selectors/authSelectors';
import { ECallsDetailSteps } from '../WizardCallsDetail/callsDetailConstants';
import WizardBillsDocModalOrderResult from './components/WizardBillsDocModalOrderResult';
import { TWizardBillsDocSetParamsPayload } from './types/wizardBillsDocActionTypes';
import { IWizardBillsDocOrderResult } from './types/wizardBillsDocTypes';
import { wizardBillsDocOrderResultHasSuccess } from './utils/wizardBillsDocOrderResult';
import { wizardBillsDocSetParamsAction, wizardBillsDocValidateFinishAction } from './wizardBillsDocActions';
import {
    EWizardBillsDocSteps,
    WIZARD_BILLS_DOC_HANDLE_NEXT,
    WIZARD_BILLS_DOC_SET_PARAMS,
    WIZARD_BILLS_DOC_VALIDATE_START,
} from './wizardBillsDocConsant';
import { makeSelectWizardBillsDocParam } from './wizardBillsDocSelectors';


export function* wizardBillsDocStepInitSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName: string = yield select(makeSelectWizardStepName());
    const { step } = action.payload;
    const isMainOperationsEnabled: boolean = yield select(makeSelectFeatureEnabled(FeatureNameEnum.MAIN_OPERATIONS));
    const context: string | undefined = yield select(makeSelectorBasketContext());
    const isInitialCheckboxes: boolean = yield select(makeSelectWizardBillsDocParam('isInitialCheckboxes'));

    switch (stepName) {
        case EWizardBillsDocSteps.StepChoose:
            if (
                context &&
                isMainOperationsEnabled &&
                isInitialCheckboxes &&
                typeof step.isFastBillsDocPreselect !== 'undefined' &&
                typeof step.isFastInvoicePreselect !== 'undefined'
            ) {
                yield put(wizardBillsDocSetParamsAction({
                    isInitialCheckboxes: false,
                    isOrderChecked: step.isFastBillsDocPreselect,
                    isInvoiceChecked: step.isFastInvoicePreselect,
                    isAdvanceInvoiceChecked: step.isAdvanceInvoiceAvailable,
                }));
                yield put(wizardStorageSaveStepAction({
                    params: {
                        isInitialCheckboxes: false,
                        isOrderChecked: step.isFastBillsDocPreselect,
                        isInvoiceChecked: step.isFastInvoicePreselect,
                        isAdvanceInvoiceChecked: step.isAdvanceInvoiceAvailable,
                    },
                }))
            } else if (!context && isInitialCheckboxes) {
                yield put(wizardBillsDocSetParamsAction({
                    isInitialCheckboxes: false,
                    isOrderChecked: true,
                    isInvoiceChecked: false,
                    isAdvanceInvoiceChecked: step.isAdvanceInvoiceAvailable,
                }));
            }

            yield put(wizardBillsDocSetParamsAction({
                formatsAvailable: step.formatsAvailable || [],
                formatChoose: step.formatsAvailable?.[0] ?? '',
                isActOrderAvailable: step.isActOrderAvailable,
                isAdvanceInvoiceAvailable: step.isAdvanceInvoiceAvailable,
                isAdvanceInvoiceChecked:
                    isInitialCheckboxes ? step.isAdvanceInvoiceAvailable : step.isAdvanceInvoiceChecked,
                isInvoiceAvailable: step.isInvoiceAvailable,
                isInvoiceOrderAvailable: step.isInvoiceOrderAvailable,
                orderWithDetailStatus: step.orderWithDetailStatus,
            }));
            break;
        case EWizardBillsDocSteps.StepConfirmation:
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }

    yield call(wizardBillsDocStorageRestoreSaga);
}

/*
* -------------------------------------------
*               STORAGE
* -------------------------------------------
*/

export function* wizardBillsDocStorageRestoreSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());

    const localStorageParams = localStorageState[basketSnapshotId] ?? {};

    const keys: Partial<Array<keyof TWizardBillsDocSetParamsPayload>> = [
        'formatChoose',
        'isActOrderChecked',
        'isAdvanceInvoiceChecked',
        'isInvoiceChecked',
        'isInvoiceOrderChecked',
        'isOrderChecked',
        'isOrderWithDetailChecked',
        'isFastBillsDocPreselect',
        'isFastInvoicePreselect',
    ];

    const params: TWizardBillsDocSetParamsPayload = keys.reduce((acc: Record<string, string>, cur?: string) => {
        if (!!cur && localStorageParams[cur]) {
            acc[cur] = localStorageParams[cur];
        }
        return acc;
    }, {});

    yield put(wizardBillsDocSetParamsAction(params));
}

export function* wizardBillsDocStorageSaveSaga(): SagaIterator {
    yield put(wizardStorageSaveStepAction({
        params: {
            formatChoose: yield select(makeSelectWizardBillsDocParam('formatChoose')),
            isOrderChecked: yield select(makeSelectWizardBillsDocParam('isOrderChecked')),
            isOrderWithDetailChecked: yield select(makeSelectWizardBillsDocParam('isOrderWithDetailChecked')),
            isInvoiceChecked: yield select(makeSelectWizardBillsDocParam('isInvoiceChecked')),
            isAdvanceInvoiceChecked: yield select(makeSelectWizardBillsDocParam('isAdvanceInvoiceChecked')),
            isInvoiceOrderChecked: yield select(makeSelectWizardBillsDocParam('isInvoiceOrderChecked')),
            isActOrderChecked: yield select(makeSelectWizardBillsDocParam('isActOrderChecked')),
        },
    }));
}

export function* wizardBillsDocStorageSaveOnStepBackSaga(): SagaIterator {
    const context = yield select(makeSelectorBasketContext());
    const isMainOperationsEnabled = yield select(makeSelectFeatureEnabled(FeatureNameEnum.MAIN_OPERATIONS));

    if (context && isMainOperationsEnabled) {
        yield put(wizardStorageSaveStepAction({
            params: {
                formatChoose: yield select(makeSelectWizardBillsDocParam('formatChoose')),
                isOrderChecked: yield select(makeSelectWizardBillsDocParam('isOrderChecked')),
                isOrderWithDetailChecked: yield select(makeSelectWizardBillsDocParam('isOrderWithDetailChecked')),
                isInvoiceChecked: yield select(makeSelectWizardBillsDocParam('isInvoiceChecked')),
                isAdvanceInvoiceChecked: yield select(makeSelectWizardBillsDocParam('isAdvanceInvoiceChecked')),
                isInvoiceOrderChecked: yield select(makeSelectWizardBillsDocParam('isInvoiceOrderChecked')),
                isActOrderChecked: yield select(makeSelectWizardBillsDocParam('isActOrderChecked')),
            },
        }));
    }
}

/*
* -------------------------------------------
*               VALIDATE
* -------------------------------------------
*/

export function* wizardBillsDocValidateSaga(): SagaIterator {
    let isStepValid = true;
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardBillsDocSteps.AccountsChooseStep:
            yield put(wizardBillsDocValidateFinishAction({ isStepValid: false }))
            const accountsChecked = yield select(makeSelectorInitBasketList());
            isStepValid = !!accountsChecked.length;
            break;
        case EWizardBillsDocSteps.BillsChooseStep:
            yield put(wizardBillsDocValidateFinishAction({ isStepValid: false }))
            const checkedItems = yield select(makeSelectorInitBasketList());
            isStepValid = !!checkedItems.length;
            break;
        case EWizardBillsDocSteps.StepChoose:
            const isOrderChecked = yield select(makeSelectWizardBillsDocParam('isOrderChecked'));
            const isInvoiceChecked = yield select(makeSelectWizardBillsDocParam('isInvoiceChecked'));
            const isInvoiceOrderChecked = yield select(makeSelectWizardBillsDocParam('isInvoiceOrderChecked'));
            const isActOrderChecked = yield select(makeSelectWizardBillsDocParam('isActOrderChecked'));

            isStepValid = isOrderChecked || isInvoiceChecked || isInvoiceOrderChecked || isActOrderChecked;
            break;
        case EWizardBillsDocSteps.StepConfirmation:
            isStepValid = yield call(wizardReportValidateSaga);
            break;
    }

    yield put(wizardBillsDocValidateFinishAction({ isStepValid }));
}

/*
* -------------------------------------------
*               STEP
* -------------------------------------------
*/

export function* wizardBillsDocHandleNextSaga(): SagaIterator {
    const stepName: TWizardStepNames = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardBillsDocSteps.AccountsChooseStep:
        case EWizardBillsDocSteps.BillsChooseStep:
            yield put(wizardNextAction({
                data: {},
            }));
            break;
        case EWizardBillsDocSteps.StepChoose:
            yield put(wizardNextAction({
                data: {},
            }));
            yield put(logBusinessEvent({
                eventName: ELogBusinessEventName.USER_BILLS_REPORTS_MASTER_TO_STEP_2,
                type: ELogBusinessEventType.common,
            }));
            break;
        case EWizardBillsDocSteps.StepConfirmation:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());

            const reportDestEmail: IWizardReportParam =
                yield select(makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST));
            const reportNeedCompression: IWizardReportParam =
                yield select(makeSelectWizardReportParam(EWizardReportParam.ZIP));
            const reportCompressionPassword: IWizardReportParam =
                yield select(makeSelectWizardReportParam(EWizardReportParam.ZIP_PASSWORD));

            const isOrderChecked = yield select(makeSelectWizardBillsDocParam('isOrderChecked'));
            const isInvoiceChecked = yield select(makeSelectWizardBillsDocParam('isInvoiceChecked'));
            const isInvoiceOrderChecked = yield select(makeSelectWizardBillsDocParam('isInvoiceOrderChecked'));
            const isActOrderChecked =  yield select(makeSelectWizardBillsDocParam('isActOrderChecked'));
            if (isOrderChecked) {
                yield put(logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_BILLS_REPORTS_MASTER_CONFIRM,
                    type: ELogBusinessEventType.common,
                }));
            }
            if (isInvoiceChecked) {
                yield put(logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_FUCTURE_REPORTS_MASTER_CONFIRM,
                    type: ELogBusinessEventType.common,
                }));
            }
            if (isInvoiceOrderChecked) {
                yield put(logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_INVOICE_REPORTS_MASTER_CONFIRM,
                    type: ELogBusinessEventType.common,
                }));
            }
            if (isActOrderChecked) {
                yield put(logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_FUCT_ACT_REPORTS_MASTER_CONFIRM,
                    type: ELogBusinessEventType.common,
                }));
            }

            const data = removeEmptyValuesFromObject({
                // data from StepChoose
                formatChoose: yield select(makeSelectWizardBillsDocParam('formatChoose')),
                isOrderChecked,
                isOrderWithDetailChecked: yield select(makeSelectWizardBillsDocParam('isOrderWithDetailChecked')),
                isInvoiceChecked,
                isAdvanceInvoiceChecked: yield select(makeSelectWizardBillsDocParam('isAdvanceInvoiceChecked')),
                isInvoiceOrderChecked,
                isActOrderChecked,
                // data from StepConfirmation
                destEmail: reportDestEmail.isEnabled ? reportDestEmail.value : '',
                needCompression: reportNeedCompression.value,
                compressionPassword: reportCompressionPassword.value,
            });

            const params = isEmpty(reportParams.notify) ? {} : reportParams;

            yield put(wizardNextAction({
                data,
                ...params,
            }));
            break;
    }
}

export function* wizardBillsDocNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    if (stepName === ECallsDetailSteps.Final) {
        const orderResult: IWizardBillsDocOrderResult = action.payload.currentStep?.step.orderResult ?? {};

        const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
        const goBack = () => breadCrumbGoBack(breadCrumbsData);

        if (wizardBillsDocOrderResultHasSuccess(orderResult)) {

            yield put(modalOpen({
                onCrossClickCallback: goBack,
                render: (props: IModalRenderProps) => createElement(WizardBillsDocModalOrderResult, {
                    onCloseModal: () => {
                        props.onCloseModal();
                        goBack();
                    },
                    orderResult,
                }),
                modalParams: {
                    closeable: false,
                },
            }));

            const { orderId } = orderResult;
            if (orderResult.billsCount) {
                yield put(logBusinessEvent({
                    type: ELogBusinessEventType.wizard,
                    eventName: ELogBusinessEventName.B2B_BILLS_REPORTS_MASTER_CONFIRM,
                    orderId,
                }));
            }
            if (orderResult.fuctsCount) {
                yield put(logBusinessEvent({
                    type: ELogBusinessEventType.wizard,
                    eventName: ELogBusinessEventName.B2B_FUCTURE_REPORTS_MASTER_CONFIRM,
                    orderId,
                }));
            }
            if (orderResult.invoicesCount) {
                yield put(logBusinessEvent({
                    type: ELogBusinessEventType.wizard,
                    eventName: ELogBusinessEventName.B2B_INVOICE_REPORTS_MASTER_CONFIRM,
                    orderId,
                }));
            }
            if (orderResult.actsCount) {
                yield put(logBusinessEvent({
                    type: ELogBusinessEventType.wizard,
                    eventName: ELogBusinessEventName.B2B_FUCT_ACT_REPORTS_MASTER_CONFIRM,
                    orderId,
                }));
            }
        } else {
            const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());
            yield put(modalOpen({
                render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title: translationCommon.error.codes['wizard.bills-doc.no-doc'],
                    translation: translationCommon.modal,
                }),
            }));
        }
    }
}

/*
* -------------------------------------------
*              Failure Handler
* -------------------------------------------
*/

export function* wizardBillsDocFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const wizardTranslation: IWizardTranslation = yield select(makeSelectWizardTranslation());
        title = translationCommon.error.codes[errorCode]
            || wizardTranslation.wizardModalError.default;
    }

    yield put(modalOpen({
        render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
            onCloseModal: props.onCloseModal,
            title,
            translation: translationCommon.modal,
        }),
    }));
}


export function* wizardBillsDocSagas(): SagaIterator {
    yield all([
        takeEvery(WIZARD_META_SUCCESS, wizardBillsDocStepInitSaga),
        takeEvery([
            WIZARD_BILLS_DOC_SET_PARAMS,
            WIZARD_BILLS_DOC_VALIDATE_START,
            WIZARD_REPORT_SET_PARAM,
        ], wizardBillsDocValidateSaga),
        takeEvery(WIZARD_BILLS_DOC_HANDLE_NEXT, wizardBillsDocHandleNextSaga),
        takeEvery(WIZARD_NEXT_ACTION, wizardBillsDocStorageSaveSaga),
        takeEvery(WIZARD_BACK_ACTION, wizardBillsDocStorageSaveOnStepBackSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, wizardBillsDocNextSuccessSaga),
        takeEvery(WIZARD_BACK_SUCCESS, wizardBillsDocStorageRestoreSaga),
        takeEvery([
            WIZARD_BACK_FAILED,
            WIZARD_NEXT_FAILED,
            WIZARD_RESTORE_FAILED,
            WIZARD_START_FAILED,
        ], wizardBillsDocFailedSaga),
    ]);
}
